import EmailLinks from './EmailLinks';
import React, { useEffect, useState } from 'react';
import { emailApi, formatApiError } from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

export function emailDocument(html) {
  // Keep mail markup isolated; opening a message must not load remote trackers.
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"><meta name="referrer" content="no-referrer"><style>body{font:15px/1.6 Arial,sans-serif;overflow-wrap:anywhere;margin:16px;color:#111418}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${html}</body></html>`;
}

export default function EmailReader({ mail, onClose, onRead, onReply }) {
  const [message, setMessage] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const uid = mail?.uid;

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    setMessage(null);
    setError('');
    emailApi.getMessage(uid).then(response => {
      if (cancelled) return;
      setMessage(response.data);
      if (response.data.seen) onRead(uid);
    }).catch(e => { if (!cancelled) setError(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [uid, attempt, onRead]);

  const current = message?.uid === String(uid) ? message : null;
  const header = current || mail;
  return (
    <Dialog open={!!mail} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" aria-describedby="email-message-metadata">
        <DialogHeader><DialogTitle>{header?.subject || '(Kein Betreff)'}</DialogTitle></DialogHeader>
        <div id="email-message-metadata" className="text-sm space-y-1 border-b border-[#E2E4E9] pb-3 break-words">
          <p><strong>Von:</strong> {header?.from_name} {header?.from_email && `<${header.from_email}>`}</p>
          {current?.to && <p><strong>An:</strong> {current.to}</p>}
          {header?.date_iso && <p><strong>Datum:</strong> {new Date(header.date_iso).toLocaleString('de-DE')}</p>}
        </div>
        {error ? (
          <div role="alert" className="space-y-3"><p className="text-red-700">{error}</p><Button type="button" variant="outline" onClick={() => setAttempt(n => n + 1)}>Erneut versuchen</Button></div>
        ) : !current ? <p role="status">Nachricht wird geladen …</p> : <>
          {current.body_html ? <>
            <iframe title="E-Mail-Inhalt" sandbox="" referrerPolicy="no-referrer" srcDoc={emailDocument(current.body_html)} className="w-full h-[50vh] border rounded-sm bg-white" />
            <p className="text-xs text-[#8A94A6]">Externe Bilder werden beim Lesen nicht geladen.</p>
          </> : <div className="whitespace-pre-wrap break-words text-sm min-h-[150px]" data-testid="email-body">{current.body_plain || 'Diese E-Mail enthält keinen Nachrichtentext.'}</div>}
          {current.attachments?.length > 0 && <section className="border-t border-[#E2E4E9] pt-3 space-y-2">
            <h3 className="font-semibold">Anhänge ({current.attachments.length})</h3>
            <ul className="space-y-2">{current.attachments.map(file => <li key={file.id}><a href={emailApi.attachmentUrl(uid, file.id)} className="text-sm underline break-all">{file.filename}</a><span className="text-xs text-[#8A94A6] ml-2">{Math.max(1, Math.ceil(file.size / 1024))} KB</span></li>)}</ul>
          </section>}
        </>}
        {current && <EmailLinks key={current.uid} message={current} onChange={values=>setMessage(m=>({...m,...values}))}/>}
        <div className="flex justify-end gap-2">{onReply && <Button type="button" className="bg-[#E60000] text-white hover:bg-[#CC0000]" disabled={!current || !(current.reply_to_email || current.from_email)} onClick={() => onReply(current)}>Antworten</Button>}<Button type="button" variant="outline" onClick={onClose}>Schließen</Button></div>
      </DialogContent>
    </Dialog>
  );
}
